import type { DragEvent } from 'react';
import type { FieldType, InputType } from '../../models/form';

/**
 * Stable designer drag payload shared by the palette, fields and layouts.
 *
 * Some DNN skins, browser extensions and Chromium/WebForms combinations strip
 * custom DataTransfer MIME values. A module-local fallback keeps the active
 * payload available until dragend, while text/plain remains the cross-browser
 * wire format for normal HTML5 drag/drop.
 */
export type DesignerDragPayload =
  | { kind: 'field'; type: FieldType; inputType?: InputType }
  | { kind: 'layout'; columns: number }
  | { kind: 'move'; fieldId: string }
  | { kind: 'move-layout'; layoutId: string };

let activePayload: DesignerDragPayload | null = null;
const validKinds = new Set<DesignerDragPayload['kind']>(['field', 'layout', 'move', 'move-layout']);

function parsePayload(raw: string): DesignerDragPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<DesignerDragPayload>;
    return parsed && validKinds.has(parsed.kind as DesignerDragPayload['kind'])
      ? parsed as DesignerDragPayload
      : null;
  } catch {
    return null;
  }
}

export function writeDesignerDrag(event: DragEvent<HTMLElement>, payload: DesignerDragPayload, effectAllowed: 'copy' | 'move'): void {
  activePayload = payload;
  const serialized = JSON.stringify(payload);
  event.dataTransfer.effectAllowed = effectAllowed;
  // Keep both formats. The custom type avoids collisions; text/plain works in
  // browsers and embedded DNN shells that reject custom MIME values.
  try { event.dataTransfer.setData('application/x-df-designer', serialized); } catch { /* fallback below */ }
  try { event.dataTransfer.setData('text/plain', serialized); } catch { /* module fallback remains active */ }
}

export function readDesignerDrag(event: DragEvent): DesignerDragPayload | null {
  let raw = '';
  try {
    raw = event.dataTransfer.getData('application/x-df-designer')
      || event.dataTransfer.getData('text/plain');
  } catch {
    // A restrictive host may deny getData. Use the active in-memory payload.
  }
  return parsePayload(raw) ?? activePayload;
}

export function clearDesignerDrag(): void {
  activePayload = null;
}
